import { useMemo } from 'react';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { ActionDisplayNumberProvider } from '../../lib/proposals/hooks/ActionDisplayNumberContext';
import { useDeliveryState } from '../../lib/proposals/linkedin/DeliveryStatesContext';
import type { SelectionState } from '../../lib/proposals/types/selection';
import { buildActionDisplayNumbering } from '../../lib/proposals/utils/actionDisplayNumbers';
import { groupActionsByType } from '../../lib/proposals/utils/actionGrouping';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import { ActionAccordion } from './ActionAccordion';
import { ActionGroupRenderer } from './ActionGroupRenderer';
import { DeliveryStatusBadge } from './DeliveryStatusBadge';
import { EmailHeaderRight } from './EmailHeaderRight';
import { AccordionActionContent } from './multiActionTimeline.helpers';
import { rendersOwnChrome } from './multiActionTimeline.utils';
import { isWideMixedGroup } from './shared';

export interface MultiActionTimelineProps {
  proposal: ProposalDetail;
  onUpdate: (proposal: ProposalDetail) => void;
  isProcessing: boolean;
  /** Callback when user clicks "View Thread" - opens the thread panel */
  onOpenThread?: () => void;
  /** Optimistic message to show immediately when user submits chat */
  optimisticMessage?: string | null;
  /** Selection state for each action (only used for multi-action proposals) */
  selectionState?: SelectionState;
  /**
   * Callback for updating actions using a functional updater pattern.
   * Receives an updater function that is called with each action.
   * The updater should return the updated action if it matches, or null to keep unchanged.
   * This avoids capturing stale proposal state in closures.
   */
  onActionUpdate?: (
    actionUpdater: (action: ProposedAction) => ProposedAction | null
  ) => void;
}

// ─── Main component ─────────────────────────────────────────────────────────

/**
 * Container component that displays all actions in a proposal.
 * Uses table view for homogeneous CRM groups (5+ same-type actions).
 * Uses accordion view for other actions.
 * If there's only one action, renders without the accordion wrapper.
 */
export function MultiActionTimeline({
  proposal,
  onUpdate,
  isProcessing,
  onOpenThread,
  optimisticMessage,
  selectionState,
  onActionUpdate,
}: MultiActionTimelineProps) {
  // Group actions by type for display
  const typedGroups = useMemo(
    () => groupActionsByType(proposal.actions),
    [proposal.actions]
  );

  // The numbers shown beside actions are positions in THIS list, so they are
  // assigned here — the only place that sees every group in render order.
  // Deriving them per group (or from execution_order) is what left gaps when
  // an action was hidden or merged away.
  const numbering = useMemo(
    () => buildActionDisplayNumbering(typedGroups),
    [typedGroups]
  );

  // Show checkboxes only for multi-action proposals
  const showCheckboxes =
    proposal.actions.length > 1 && selectionState !== undefined;

  // Memoized headerRight for the single-action path below. Computed
  // unconditionally (before any early return) per the Rules of Hooks —
  // `soleAction` is undefined whenever there isn't exactly one action, in
  // which case this memo is simply unused.
  const soleAction =
    proposal.actions.length === 1 ? proposal.actions[0] : undefined;
  const soleDelivery = useDeliveryState(soleAction?.id);
  const soleActionHeaderRight = useMemo(() => {
    const badge = soleDelivery ? (
      <DeliveryStatusBadge state={soleDelivery} />
    ) : undefined;
    const emailRight =
      soleAction?.proposed_action_type_name === 'gmail_send' ? (
        <EmailHeaderRight isProcessing={isProcessing} />
      ) : undefined;
    if (badge && emailRight) {
      return (
        <span className='flex items-center gap-2'>
          {badge}
          {emailRight}
        </span>
      );
    }
    return badge ?? emailRight;
  }, [soleAction, isProcessing, soleDelivery]);

  // If only one action, render inside accordion but without checkbox
  if (proposal.actions.length === 1) {
    const action = proposal.actions[0];

    // These actions render their own chrome — skip the accordion shell
    if (rendersOwnChrome(action)) {
      return (
        <div className='flex flex-col gap-4 w-full' data-action-id={action.id}>
          <AccordionActionContent
            action={action}
            proposal={proposal}
            onUpdate={onUpdate}
            onActionUpdate={onActionUpdate}
            isProcessing={isProcessing}
            onOpenThread={onOpenThread}
            optimisticMessage={optimisticMessage}
            isSoleAction={true}
          />
        </div>
      );
    }

    return (
      <div className='flex flex-col gap-4 w-full' data-action-id={action.id}>
        <ActionAccordion
          action={action}
          showCheckbox={false}
          showExecutionOrder={false}
          isSelected={true}
          isProcessing={isProcessing}
          orgId={proposal.org}
          headerRight={soleActionHeaderRight}
        >
          <AccordionActionContent
            action={action}
            proposal={proposal}
            onUpdate={onUpdate}
            onActionUpdate={onActionUpdate}
            isProcessing={isProcessing}
            onOpenThread={onOpenThread}
            optimisticMessage={optimisticMessage}
            isSoleAction={true}
          />
        </ActionAccordion>
      </div>
    );
  }

  // Use flex-fill layout when all groups are WIDE tables, so they expand to
  // fill available space with internal scrolling. Otherwise use default flow
  // layout (parent handles scrolling via overflow-y-auto). Mirrors
  // `ProposalContent`'s `isTableProposal` — see `isWideMixedGroup`'s doc
  // comment for why a 'mixed' group only counts when it renders the wide
  // homogeneous shape, not its default single-Action-column shape.
  const hasOnlyTables =
    typedGroups.length > 0 &&
    typedGroups.every(
      g =>
        g.viewType === 'table' ||
        (g.viewType === 'mixed' && isWideMixedGroup(g.actions))
    );

  return (
    <ActionDisplayNumberProvider numbering={numbering}>
      <div
        className={`flex flex-col gap-4 w-full ${hasOnlyTables ? 'flex-1' : ''}`}
      >
        {/*
        Key on the group's first action, not `group.id`. `group.id` is a
        positional counter (`type-group-N`) assigned while grouping, and CRM
        groups are counted before non-CRM ones — so anything that changes the
        number of CRM groups (e.g. re-targeting an association, which can
        split or merge a connected component) renumbers the groups after it
        and remounts them, email editor included. Each action belongs to
        exactly one group, so its first action is a unique, stable identity.
      */}
        {typedGroups.map(group => (
          <ActionGroupRenderer
            key={getStableActionKey(group.actions[0])}
            group={group}
            proposal={proposal}
            onUpdate={onUpdate}
            onActionUpdate={onActionUpdate}
            isProcessing={isProcessing}
            selectionState={selectionState}
            showCheckboxes={showCheckboxes}
            hasOnlyTables={hasOnlyTables}
            onOpenThread={onOpenThread}
            optimisticMessage={optimisticMessage}
          />
        ))}
      </div>
    </ActionDisplayNumberProvider>
  );
}
